import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = authenticated
  const [ready, setReady] = useState(false);

  // Session lives in httpOnly cookies set by the backend — nothing sensitive is kept in web storage.
  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    const clean = { id: data.id, email: data.email, name: data.name, role: data.role };
    setUser(clean);
    return clean;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout request failed; clearing local session anyway", err); }
    setUser(false);
  }, []);

  // Memoize context value — prevents every consumer from re-rendering on every
  // AuthProvider parent re-render due to a fresh object literal on each pass.
  const value = useMemo(
    () => ({ user, ready, login, logout, refresh: fetchMe }),
    [user, ready, login, logout, fetchMe]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
